import React, { useState, useEffect } from 'react';
import { Form, FormGroup, FormText, Button } from 'reactstrap';
import Axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faArrowLeft, faTrash } from '@fortawesome/free-solid-svg-icons';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document';
import moment from 'moment';

import ImageFileFieldGroup from '../../Common/ImageFileFieldGroup';
import TextFieldGroup from '../../Common/TextFieldGroup';
import CheckboxFieldGroup from '../../Common/CheckboxFieldGroup';
import ProgressIndicator from '../../Common/ProgressIndicator';
import { Event } from '../../../Data/Classes';
import DateTimePickerFieldGroup from '../../Common/DateTimePickerFieldGroup';

function EventEditForm(props) {
    const [isProgress, setIsProgress] = useState(false);
    const [error, setError] = useState({});
    const [event, setEvent] = useState(null);

    let editorC = null;

    useEffect(() => {
        setEvent({
            ...props.event,
            date: moment(new Date(props.event.date)),
            registrationStartDate: moment(new Date(props.event.registrationStartDate)),
            registrationDeadline: moment(new Date(props.event.registrationDeadline)),
            steinOrderDeadline: moment(new Date(props.event.steinOrderDeadline))
        });
    }, []);

    function onChange(e) {
        setEvent({ ...event, [e.target.name]: e.target.value });
    }

    function onCheckedChange(e) {
        setEvent({ ...event, [e.target.name]: e.target.checked });
    }

    function setImage(image) {
        setEvent({ ...event, previewPicture: image });
    }

    function onDeleteClick(e) {
        if (window.confirm("Biztos, hogy törlöd a rendezvényt?\nA művelet nem visszavonható.")) {
            setIsProgress(true);
            Axios.delete(`/admin/events/${event.id}`)
                .then(res => {
                    setIsProgress(false);
                    toast.success(`${event.title} törölve.`);
                    window.history.back();
                }, err => {
                    setIsProgress(false);
                    toast.error("A rendezvény törlése sikertelen volt.");
                });
        }
    }

    function onSubmit(e) {
        e.preventDefault();
        setIsProgress(true);
        Axios.post(`/admin/events/${event.id}`,
            new Event(event.id,
                event.name,
                event.venue,
                new Date(event.date.toDate().setSeconds(0)),
                event.description,
                event.previewPicture,
                event.registrationLimit,
                new Date(event.registrationStartDate.toDate().setSeconds(0)),
                new Date(event.registrationDeadline.toDate().setSeconds(0)),
                event.cost,
                event.stein,
                event.stein ? event.steinCost : 0,
                event.stein ? new Date(event.steinOrderDeadline.toDate().setSeconds(0)) : null,
                event.guestsPerUserLimit,
                event.public,
                event.organisersPublished,
                event.onlyFreshmenCanRegister,
                event.deleted))
            .then(res => {
                setIsProgress(false);
                toast.success(`${event.name} elmentve.`);
                window.history.back();
            }, err => {
                setIsProgress(false);
                setError(err.errors);
                toast.error("A rendezvény mentése sikertelen volt.");
            });
    }


    if (isProgress)
        return <ProgressIndicator />;

    if (event) {
        const steinFields = (
            <FormGroup row>
                <TextFieldGroup className="col-md"
                    field="steinCost"
                    onChange={onChange}
                    label="Korsó ára"
                    error={error.SteinCost}
                    value={event.steinCost}
                />
                <DateTimePickerFieldGroup className="col-md"
                    field="steinOrderDeadline"
                    onChange={onChange}
                    type="datetime-local"
                    label="Korsórendelési határidő"
                    error={error.SteinOrderDeadline}
                    value={event.steinOrderDeadline}
                />
            </FormGroup>
        );

        return (
            <Form onSubmit={onSubmit} id="eventEditForm">
                <FormGroup>
                    <FormGroup row>
                        <TextFieldGroup className="col-md"
                            field="name"
                            onChange={onChange}
                            label="Név"
                            error={error.Name}
                            value={event.name}
                        />
                        <TextFieldGroup className="col-md"
                            field="venue"
                            onChange={onChange}
                            label="Helyszín"
                            error={error.Venue}
                            value={event.venue}
                        />
                        <TextFieldGroup className="col-md"
                            field="cost"
                            onChange={onChange}
                            type="number"
                            label="Részvételi díj"
                            error={error.Cost}
                            value={event.cost}
                        />
                    </FormGroup>
                    <FormGroup row>
                        <DateTimePickerFieldGroup className="col-md"
                            field="date"
                            onChange={onChange}
                            type="datetime-local"
                            label="Dátum"
                            error={error.Date}
                            value={event.date}
                        />
                        <DateTimePickerFieldGroup className="col-md"
                            field="registrationStartDate"
                            onChange={onChange}
                            type="datetime-local"
                            label="Jelentkezés kezdete"
                            error={error.RegistrationStartDate}
                            value={event.registrationStartDate}
                        />
                        <DateTimePickerFieldGroup className="col-md"
                            field="registrationDeadline"
                            onChange={onChange}
                            type="datetime-local"
                            label="Jelentkezési határidő"
                            error={error.RegistrationDeadline}
                            value={event.registrationDeadline}
                        />
                    </FormGroup>
                    <FormGroup row>
                        <TextFieldGroup className="col-md"
                            field="registrationLimit"
                            onChange={onChange}
                            type="number"
                            label="Max. résztvevői létszám"
                            error={error.RegistrationLimit}
                            value={event.registrationLimit}
                        />
                        <TextFieldGroup className="col-md"
                            field="guestsPerUserLimit"
                            onChange={onChange}
                            type="number"
                            label="Külsős vendégek száma jelentkezőnként"
                            error={error.GuestsPerUserLimit}
                            value={event.guestsPerUserLimit}
                        />
                    </FormGroup>
                    <FormGroup>
                        <label>Leírás</label>
                        <CKEditor
                            editor={DecoupledEditor}
                            data={event.description}
                            onReady={editor => {
                                editor.ui.getEditableElement().parentElement.insertBefore(
                                    editor.ui.view.toolbar.element,
                                    editor.ui.getEditableElement()
                                );

                                editorC = editor;
                            }}
                            onError={(error, { willEditorRestart }) => {
                                if (willEditorRestart) {
                                    editorC.ui.view.toolbar.element.remove();
                                }
                            }}
                            onChange={(ev, editor) => {
                                setEvent({ ...event, description: editor.getData() });
                            }}
                        />
                        {error.Description && <FormText>{error.Description}</FormText>}
                    </FormGroup>
                    <FormGroup row>
                        <ImageFileFieldGroup className="col-lg"
                            field="previewPicture"
                            setValue={setImage}
                            label="Háttérkép"
                            error={error.PreviewPicture}
                            value={event.previewPicture}
                            maxWidth={600}
                            maxHeight={350} />
                    </FormGroup>
                    <CheckboxFieldGroup
                        field="stein"
                        onChange={onCheckedChange}
                        label="Korsó rendelhető"
                        error={error.Stein}
                        value={event.stein}
                    />
                    {event.stein && steinFields}
                    <CheckboxFieldGroup
                        field="public"
                        onChange={onCheckedChange}
                        label="Nyilvános"
                        error={error.Public}
                        value={event.public}
                    />
                    <CheckboxFieldGroup
                        field="organisersPublished"
                        onChange={onCheckedChange}
                        label="Rendezői lista nyilvános"
                        error={error.OrganisersPublished}
                        value={event.organisersPublished}
                    />
                    <CheckboxFieldGroup
                        field="onlyFreshmenCanRegister"
                        onChange={onCheckedChange}
                        label="Csak gólyák jelentkezhetnek"
                        error={error.OnlyFreshmenCanRegister}
                        value={event.onlyFreshmenCanRegister}
                    />
                </FormGroup>

                {error[""]}
                <Button color="primary" type="submit"><FontAwesomeIcon icon={faCheck} /> Mentés</Button>
                <Button color="primary" type="button" onClick={(e) => window.history.back()}><FontAwesomeIcon icon={faArrowLeft} /> Mégsem</Button>
                <Button color="warning" type="button" onClick={onDeleteClick} className="float-right"><FontAwesomeIcon icon={faTrash} /> Törlés</Button>
            </Form >
        );
    }

    return null;
}

export default EventEditForm;
